import React, { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ValidationError } from 'yup';

import { GroupSelectItemInterface, GroupSelectOptionsInterface } from '@components/common';
import Tabs, { ITabItem } from '@components/common/Tabs/Tabs';

import { useCreateKpiAlert } from '@hooks/kpiAlerts/useCreateKpiAlert';
import { useKPI } from '@hooks/kpis/useKPI';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { isEmpty, normalizeObject } from '@utils/common';

import { IThreshold } from '@interfaces/alarms/threshold';
import {
	EOranKpiAlertRecipientTypes,
	ICreateKpiAlertDTO,
	IOranNode,
} from '@interfaces/kpi/dto/kpiAlertsDTO';
import { IKPISite } from '@interfaces/kpi/kpiCommon';
import { IKpiAlertSchema } from './interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ESnackbarTypes, EVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';
import { EKpiAlertsStatus, ESeverity } from '@enums/kpi-common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';
import { EAlarmManagementFields } from '@components/ModalComponents/AlarmManagementForm/enums';
import { EKpiBasicInformationFields } from './AddKpiAlertBasicInformation/enums';
import { EApplyFor, EConditionFields, EThresholdSource } from './AddKpiAlertCondition/enums';
import { EScheduleFields } from './AddKpiAlertSchedule/enums';

import { responseMessages } from '@constants/responseMessages';
import { ETabIdsAndFields, initialTabs } from './constants';

import { ECheckForFields } from './AddKpiAlertCheckFor/enum';
import { EAllowToRenotify, ENotify } from './AddKpiAlertSchedule/AddKpiAlertSchedule';
import { schema } from './schema';
import { ETabsIds } from './tabsIds';
import { filterValuesByKeys } from './utils';

import './AddNewKpiAlertRule.scss';

// Base props common to both cases
export type IAddNewKpiAlertBaseProps = {
	closeModal: () => void;
	defaultValues?: IKpiAlertSchema;
	isLoading?: boolean;
	setIsDirtyForm?: (isDirty: boolean) => void;
	positiveBtnText: string;
	negativeBtnText: string;
}

// Full interface with conditional requirements
export type IAddNewKpiAlertProps =
	| (IAddNewKpiAlertBaseProps & {
	isEdit: true;
	handleEditRule: (formValues: IKpiAlertSchema) => void;
})
	| (IAddNewKpiAlertBaseProps & {
	isEdit?: false;
	handleEditRule?: (formValues: IKpiAlertSchema) => void;
});

function AddNewKpiAlertRule({
	closeModal,
	defaultValues,
	isLoading,
	handleEditRule,
	isEdit,
	setIsDirtyForm,
	positiveBtnText,
	negativeBtnText,
}: IAddNewKpiAlertProps) {
	const dispatch = useAppDispatch();

	const [activeStep, setActiveStep] = useState<string>(ETabsIds.basicInformation);

	const {
		optionsVendorsKpi,
	} = useKPI();

	const {
		formState,
		setValue,
		getValues,
		watch,
		register,
		setError,
		clearErrors,
		handleSubmit,
		control,
		reset,
		trigger,
		resetField,
		getFieldState,
		unregister,
		setFocus,
	} = useForm({
		resolver: yupResolver(schema),
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onBlur,
		defaultValues: {
			[EKpiBasicInformationFields.status]: EKpiAlertsStatus.active,
			[EKpiBasicInformationFields.severity]: ESeverity.critical,
			[EKpiBasicInformationFields.ruleName]: '',
			[EKpiBasicInformationFields.ruleDescription]: '',
			[ECheckForFields.aggregationType]: defaultValues?.aggregationType || EAggregationTypes.aggregated,
			[ECheckForFields.sitesT_4G_FDD]: [],
			[ECheckForFields.sitesT_4G_TDD]: [],
			[ECheckForFields.sitesT_5G]: defaultValues?.sitesT_5G || [],
			[ECheckForFields.kpi]: defaultValues?.kpi || [],
			[ECheckForFields.vendor]: optionsVendorsKpi
				.find((vendor: TSelectOptionType & { isDefault: boolean }) => vendor.isDefault),
			[EConditionFields.thresholdSource]: EThresholdSource.setManually,
			[EConditionFields.applyFor]: EApplyFor.allBuiltCharts,
			[EConditionFields.checkEveryHours]: null,
			[EConditionFields.checkEveryMinutes]: null,
			[EConditionFields.evaluationWindowHours]: null,
			[EConditionFields.evaluationWindowMinutes]: null,
			[EScheduleFields.notify]: ENotify.immediately,
			[EScheduleFields.numberOfRepetitions]: null,
			[EScheduleFields.allowToRenotify]: EAllowToRenotify.dont,
			[EScheduleFields.allowToRenotifyAfterCustomValueHours]: null,
			[EScheduleFields.allowToRenotifyAfterCustomValueMinutes]: null,
			[EAlarmManagementFields.roles]: null,
			[EAlarmManagementFields.users]: null,
			...defaultValues,
		},
	});

	const values = getValues();

	useEffect(() => {
		if (setIsDirtyForm) {
			setIsDirtyForm(formState.isDirty);
		}
	}, [formState.isDirty, setIsDirtyForm]);

	useEffect(() => {
		if (!isEmpty(defaultValues) && values.kpi?.length === 0) {
			reset({
				...defaultValues,
			});
		}
	}, [defaultValues, reset, values.kpi?.length]);

	useEffect(() => {
		const erroredTabIds = Object.keys(formState.errors).reduce<Set<string>>((acc, fieldName) => {
			const tabId = Object.entries(ETabIdsAndFields).find(([_, fields]) => fields.includes(fieldName))?.[0];

			if (tabId) {
				acc.add(tabId);
			}

			return acc;
		}, new Set<string>());

		if (erroredTabIds.size > 0) {
			setTabs(prevValue => {
				return prevValue.map(tab => {
					return {
						...tab,
						isError: erroredTabIds.has(tab.id),
					};
				});
			});
		}
	}, [formState.errors]);

	const [tabs, setTabs] = useState(() => {
		return initialTabs.map((tab: ITabItem): ITabItem => {
			return {
				...tab,
				isActive: tab.id === activeStep,
				isDisabled: tab.id !== activeStep && !isEdit,
			};
		});
	});

	const validateActiveTabFields = useCallback(async () => {
		const activeTab: ITabItem | undefined = tabs.find((tab: ITabItem) => tab.id === activeStep);

		const tabValues: Record<string, any> = filterValuesByKeys(getValues(), activeTab?.fields || []);

		return await activeTab?.schema?.validate(tabValues, { abortEarly: false });
	}, [activeStep, getValues, tabs]);

	const handleSetTabs = useCallback(async (activeTabId: string) => {
		try {
			await validateActiveTabFields();

			setActiveStep(activeTabId);

			setTabs((prevTabs: ITabItem[]) => {
				return prevTabs.map((tab: ITabItem): ITabItem => {
					const isActive = tab.id === activeTabId;

					return {
						...tab,
						isActive,
						isDisabled: !isActive && !isEdit,
					};
				});
			});
		} catch (e) {
			const { message, path, inner } = e as ValidationError;

			if (path) {
				setError(path as any, { message: message as string });
			} else {
				inner.forEach((item: any) => {
					setError(item.path, { message: item.message });
				});
			}

			setActiveStep(activeTabId);

			setTabs((prevTabs: ITabItem[]) => {
				return prevTabs.map((tab: ITabItem): ITabItem => {
					const isActive = tab.id === activeTabId;

					return {
						...tab,
						isActive,
						isDisabled: !isActive && !isEdit,
					};
				});
			});
		}
	}, [isEdit, setError, validateActiveTabFields]);

	const getSites = (formValues: IKpiAlertSchema): {
		sites: IKPISite[];
		technologies: string[];
		nodes: IOranNode[];
	} => {
		let sites: IKPISite[] = [];
		const technologies: string[] = [];
		const isOranVendor = formValues.vendor.value === EVendorsValues.oran;

		const nodes: IOranNode[] = isOranVendor && formValues?.sitesT_5G
			? formValues.sitesT_5G.reduce<IOranNode[]>((acc, curr) => {
				const nestedNodes = curr.items.reduce<IOranNode[]>((nestedAcc, nestedCurr) => {
					const nodeId = nestedCurr.category?.name;
					const cellIds = nestedCurr?.items?.map((item: GroupSelectItemInterface) => item.id) || [];
					const node: IOranNode = {
						nodeId: nodeId || '',
						cellIds: cellIds.filter((id): id is string => id !== undefined),
					};

					return [...nestedAcc, node];
				}, []);

				return [...acc, ...nestedNodes];
			}, [])
			: [];

		for (const [key, values] of Object.entries(formValues)) {
			if (key.includes('sites') && !isEmpty(values)) {
				sites = [
					...sites,
					...values.map((item: GroupSelectOptionsInterface) => {
						return {
							siteId: item.category.name,
							cellIds: item.items.map((cell: GroupSelectItemInterface) => cell.id),
							technology: key.replace('sites', ''),
						};
					}),
				];
			}

			if (key.includes('sitesT_') && !isEmpty(values)) {
				technologies.push(key.replace('sites', ''));
			}
		}

		return {
			sites,
			technologies,
			nodes,
		};
	};

	const onSuccess = useCallback(() => {
		dispatch(setSnackbar({
			message: `A new ${values[EKpiBasicInformationFields.ruleName]} has been created!`,
			type: ESnackbarTypes.success,
		}));

		closeModal();
	}, [closeModal, dispatch, values]);

	const onError = useCallback(() => {
		dispatch(setSnackbar({
			message: responseMessages.error,
			type: ESnackbarTypes.error,
		}));
	}, [dispatch]);

	const { createKpiAlertRule, isLoadingCreateKpiAlertRule } = useCreateKpiAlert({
		onSuccess,
		onError,
		vendor: values?.vendor?.value,
	});

	const handleAddKpiAlertRule = useCallback(
		(formValues: IKpiAlertSchema) => {
			const isOranVendor = formValues.vendor.value === EVendorsValues.oran;

			const { sites, technologies, nodes } = getSites(formValues);

			const thresholds = formValues.thresholds.map((threshold: IThreshold) => {
				return {
					minThreshold: threshold.minThreshold,
					maxThreshold: threshold.maxThreshold,
					kpiId: threshold.kpiId,
					...(!isOranVendor && { kpiName: threshold.kpiName }),
				};
			});

			const kpiIds: string[] = formValues.vendor.value === EVendorsValues.oran
				? formValues.kpi.filter((kpi: any) => kpi.isChecked && kpi.key !== 'All' && kpi.dbId).map((kpi: any) => kpi.dbId)
				: formValues.kpi.reduce((acc: string[], kpi: any) => {
					const kpiIds = kpi.categoryGroups
						.map((categoryGroup: any) => {
							return categoryGroup.items.map((item: any) => item.id);
						})
						.flat();

					return acc.concat(kpiIds);
				}, []);

			const alertRule: ICreateKpiAlertDTO = normalizeObject({
				aggregationType: formValues.aggregationType,
				checkEveryHours: formValues.checkEveryHours?.value,
				checkEveryMinutes: formValues.checkEveryMinutes?.value,
				description: formValues.ruleDescription,
				evaluateHours: formValues.evaluationWindowHours?.value,
				evaluateMinutes: formValues.evaluationWindowMinutes?.value,
				isActive: formValues.status === EKpiAlertsStatus.active,
				isEmail: !!formValues.isEmail,
				isNotification: !!formValues.isNotification,
				kpiIds,
				messageBody: formValues.messageBody,
				messageTitle: formValues.messageTitle,
				name: formValues.ruleName,
				notifyCondition: formValues.notify,
				renotifyCondition: formValues.allowToRenotify,
				renotifyHours: formValues.allowToRenotifyAfterCustomValueHours?.value,
				renotifyMinutes: formValues.allowToRenotifyAfterCustomValueMinutes?.value,
				roles: formValues.roles?.map((role: any) => role.value),
				severity: formValues.severity,
				thresholdApplication: formValues.applyFor,
				thresholdSource: formValues.thresholdSource,
				thresholds,
				users: formValues.users?.map((user) => user?.id) as string[],
				valuesOutsideThreshold: formValues.numberOfRepetitions,
				vendor: formValues.vendor.value,
			});

			if (isOranVendor) {
				const recipientType = formValues.roles?.length > 0
					? EOranKpiAlertRecipientTypes.group
					: EOranKpiAlertRecipientTypes.person;

				alertRule.nodes = nodes;
				alertRule.recipientType = recipientType;
			} else {
				alertRule.sites = sites;
				alertRule.technologies = technologies;
			}

			createKpiAlertRule(alertRule);
		},
		[createKpiAlertRule],
	);

	const negativeHandler = useCallback((backTabName?: string): void => {
		if (isEdit) {
			return closeModal();
		}

		if (backTabName) void handleSetTabs(backTabName);
	}, [closeModal, handleSetTabs, isEdit]);

	const positiveHandler = useCallback((formValues: IKpiAlertSchema) => {
		if (isEdit) {
			return handleEditRule(formValues);
		}

		handleAddKpiAlertRule(formValues);
	}, [handleAddKpiAlertRule, handleEditRule, isEdit]);

	return (
		<div className='addNewKpiNotification'>
			<Tabs
				contentClassName='tabContent'
				isLoading={isLoading || isLoadingCreateKpiAlertRule}
				setTabState={handleSetTabs}
				tabProps={{
					setActiveTab: handleSetTabs,
					isEdit,
					formMethods: {
						clearErrors,
						control,
						getFieldState,
						getValues,
						handleSubmit,
						register,
						reset,
						resetField,
						setError,
						setFocus,
						setValue,
						trigger,
						unregister,
						watch,
					},
					formState,
					values: getValues(),
					closeModal,
					positiveHandler,
					negativeHandler,
					positiveBtnText,
					negativeBtnText,
					isLoading,
				}}
				tabs={tabs}
			/>
		</div>
	);
}

export default memo(AddNewKpiAlertRule);
